import type { AwsS3Options } from "@uppy/aws-s3"
import type { BasePlugin, Body, Meta, UppyFile, UppyOptions } from "@uppy/core"
import type { TusOptions } from "@uppy/tus"
import type { WebcamOptions } from "@uppy/webcam"
import type { TranslationPaths } from "./useTranslation"
import AwsS3 from "@uppy/aws-s3"
import Uppy from "@uppy/core"
import Tus from "@uppy/tus"
import Webcam from "@uppy/webcam"
import { computed, ref, watchEffect } from "vue"
import { abortMultipartUpload, createMultipartUpload, getPresignedUrl, notifyMultipartUploadComplete, signUploadPart } from "@/api"
import { getDocumentPresignedUrl } from "@/api/getDocumentPresignedUrl"
import { ForbiddenException, getErrorCode, HttpException } from "@/http"
import { languageMetadata } from "@/i18n/constants"
import { useTranslation } from "./useTranslation"

type TFunction = (
  path: TranslationPaths,
  named?: Record<string, unknown>,
) => string

interface ErrorWithContext extends Error {
  context?: Record<string, unknown>
}

function createUploadError(
  error: HttpException<unknown>,
  t: TFunction,
  context: Record<string, unknown>,
) {
  const errorCode = getErrorCode(error.response?.data)
  const message = errorCode ? t(`error_codes.${errorCode}` as const) : t("something_went_wrong")
  const err = new Error(message) as ErrorWithContext

  err.context = {
    errorCode,
    originalMessage: error.message,
    ...context,
  }

  return err
}

export interface Restrictions {
  maxFileSize: number | null
  minFileSize: number | null
  maxTotalFileSize: number | null
  minNumberOfFiles: number | null
  requiredMetaFields: string[]
  allowedFileTypes: string[]
  maxNumberOfFiles: number | null
}

export interface UppyMeta extends Meta {
  eventVaultId?: number
  eventAlbumId?: number
}

export interface UppyBody extends Body {}

export interface DocumentUploadMeta extends UppyMeta {
  key?: string
  fileName?: string
  eventDocumentId?: number
}

export interface DocumentUploadBody extends Body {}

type OmitFirstArg<T> = T extends [any, ...infer U] ? U : never

const defaultRestrictions: Restrictions = {
  maxFileSize: null,
  minFileSize: null,
  maxTotalFileSize: null,
  minNumberOfFiles: null,
  requiredMetaFields: [],
  allowedFileTypes: [],
  maxNumberOfFiles: null,
}

export interface IUppyLocale {
  strings: Record<string, string | Record<number, string>>
  pluralize: (n: number) => number
}

function getIdsFromFileMeta(file: UppyFile<UppyMeta, UppyBody>): { eventVaultId?: number, albumId?: number } {
  const { meta } = file

  return {
    eventVaultId: meta.eventVaultId,
    albumId: meta.eventAlbumId,
  }
}

export function s3Plugin(uppy: Uppy<UppyMeta, UppyBody>, t: TFunction): { name: string, plugin: typeof AwsS3, options: AwsS3Options<UppyMeta, UppyBody> } {
  const name = "AwsS3"

  return {
    name,
    plugin: AwsS3,
    options: {
      id: name,
      limit: 5,
      shouldUseMultipart: file => Boolean(file?.size && file.size > 5 * 1024 * 1024),
      getUploadParameters: async (file: UppyFile<UppyMeta, UppyBody>) => {
        const { eventVaultId, albumId } = getIdsFromFileMeta(file)

        if (!eventVaultId) {
          throw new Error(t("something_went_wrong"))
        }

        try {
          const response = await getPresignedUrl(file, eventVaultId, albumId)

          if (!response.data?.key || !response.data?.fileName || !response.data?.uploadUrl) {
            throw new Error(t("something_went_wrong"))
          }

          uppy.setFileMeta(file.id, { ...file.meta, key: response.data?.key, uFileName: response.data?.fileName })

          return {
            method: "PUT",
            url: response.data?.uploadUrl ?? "",
            headers: {
              "Content-Type": file.type || "application/octet-stream",
            },
          }
        }
        catch (error) {
          if (error instanceof ForbiddenException) {
            throw createUploadError(error, t, { fileId: file.id })
          }

          throw error
        }
      },
      createMultipartUpload: async (file: UppyFile<UppyMeta, UppyBody>) => {
        const { eventVaultId, albumId } = getIdsFromFileMeta(file)

        if (!eventVaultId) {
          throw new Error(t("something_went_wrong"))
        }

        let response: Awaited<ReturnType<typeof createMultipartUpload>>

        try {
          response = await createMultipartUpload(file, eventVaultId, albumId ?? undefined)
        }
        catch (error) {
          if (error instanceof ForbiddenException) {
            throw createUploadError(error, t, { fileId: file.id })
          }

          throw error
        }

        if (!response?.data?.key || !response?.data?.fileName) {
          const err = new Error(t("uploader.error.invalid_response")) as ErrorWithContext
          err.context = { responseData: response?.data, fileId: file.id }

          throw err
        }

        uppy.setFileMeta(file.id, {
          ...file.meta,
          key: response.data.key,
          uFileName: response.data.fileName,
        })

        return {
          uploadId: response.data.uploadId,
          key: response.data.key,
        }
      },
      signPart: async (
        file: UppyFile<UppyMeta, UppyBody>,
        { uploadId, key, partNumber },
      ) => {
        const { eventVaultId } = getIdsFromFileMeta(file)

        if (!eventVaultId) {
          throw new Error(
            "Missing event vault id metadata for the provided file, unable to sign part.",
          )
        }

        const url = await signUploadPart({ uploadId, key, partNumber }, eventVaultId)

        if (!url) {
          const err = new Error(t("uploader.error.sign_part_failed")) as ErrorWithContext
          err.context = { uploadId, key, partNumber, fileId: file.id }

          throw err
        }

        return { url }
      },
      listParts: () => [],
      abortMultipartUpload: async (
        file: UppyFile<UppyMeta, UppyBody>,
        { uploadId, key },
      ) => {
        const { eventVaultId } = getIdsFromFileMeta(file)

        if (!eventVaultId) {
          throw new Error("Missing event vault id metadata for the provided file, unable to abort multipart upload.")
        }

        if (!uploadId) {
          throw new Error("Failed to abort multipart upload, missing uploadId")
        }

        await abortMultipartUpload({ uploadId, key }, eventVaultId)
      },
      completeMultipartUpload: async (file, { uploadId, key, parts }) => {
        const { eventVaultId } = getIdsFromFileMeta(file)

        if (!eventVaultId) {
          throw new Error("Missing event vault id metadata for the provided file, unable to complete multipart upload.")
        }

        const cleanedParts = parts
          .filter(part => typeof part.PartNumber === "number")
          .map(part => ({ partNumber: part.PartNumber, etag: part.ETag }))

        try {
          await notifyMultipartUploadComplete({ ...file.meta, uploadId, key, parts: cleanedParts }, eventVaultId)
        }
        catch (error) {
          if (error instanceof ForbiddenException) {
            throw createUploadError(error, t, {
              fileId: file.id,
              uploadId,
              key,
            })
          }

          throw error
        }

        return Promise.resolve({})
      },
    } satisfies AwsS3Options<UppyMeta, UppyBody>,
  }
}

export function documentS3Plugin(
  uppy: Uppy<DocumentUploadMeta, DocumentUploadBody>,
  t: TFunction,
): { name: string, plugin: typeof AwsS3, options: AwsS3Options<DocumentUploadMeta, DocumentUploadBody> } {
  const name = "DocumentAwsS3"

  return {
    name,
    plugin: AwsS3,
    options: {
      id: name,
      limit: 1,
      shouldUseMultipart: false,
      getUploadParameters: async (file: UppyFile<DocumentUploadMeta, DocumentUploadBody>) => {
        const eventVaultId = file.meta.eventVaultId

        if (!eventVaultId) {
          throw new Error(t("something_went_wrong"))
        }

        let response: Awaited<ReturnType<typeof getDocumentPresignedUrl>>

        try {
          response = await getDocumentPresignedUrl(file, eventVaultId)
        }
        catch (error) {
          if (error instanceof HttpException) {
            throw createUploadError(error, t, { fileId: file.id })
          }

          throw new Error(t("document_upload_input.upload_failed"), { cause: error })
        }

        if (!response.data?.key || !response.data?.fileName || !response.data?.uploadUrl || response.data?.eventDocumentId == null) {
          throw new Error(t("document_upload_input.invalid_upload_response"))
        }

        uppy.setFileMeta(file.id, {
          ...file.meta,
          key: response.data.key,
          fileName: response.data.fileName,
          eventDocumentId: response.data.eventDocumentId,
        })

        return {
          method: "PUT",
          url: response.data.uploadUrl,
          headers: {
            "Content-Type": file.type || "application/octet-stream",
          },
        }
      },
    } satisfies AwsS3Options<DocumentUploadMeta, DocumentUploadBody>,
  }
}

export function tusPlugin(): { name: string, plugin: typeof Tus, options: TusOptions<UppyMeta, UppyBody> } {
  const name = "Tus"

  return {
    name,
    plugin: Tus,
    options: {
      id: name,
      endpoint: import.meta.env.VITE_BASE_API_URL
        ? `${import.meta.env.VITE_BASE_API_URL}/upp`
        : `${window.location.protocol}//${window.location.host}/upp`,
      withCredentials: true,
      chunkSize: 10 * 1024 * 1024,
      limit: 5,
    } satisfies TusOptions<UppyMeta, UppyBody>,
  }
}

export function webcamPlugin(): { name: string, plugin: typeof Webcam, options: WebcamOptions<UppyMeta, UppyBody> } {
  return {
    name: "Webcam",
    plugin: Webcam,
    options: {
      modes: ["video-audio"],
      preferredVideoMimeType: "video/mp4",
    } satisfies WebcamOptions<UppyMeta, UppyBody>,
  }
}

export function useUppy<TMeta extends Meta = UppyMeta, TBody extends Body = UppyBody>(
  options: UppyOptions<TMeta, TBody> = { restrictions: defaultRestrictions } as UppyOptions<TMeta, TBody>,
) {
  const uppy = new Uppy<TMeta, TBody>()

  const { locale, messages } = useTranslation()

  const uploadError = ref<{ errorCode: string, message: string } | null>(null)

  const uppyLocale = computed<IUppyLocale>(() => {
    const currentLanguage = locale.value
    const metadata = languageMetadata[currentLanguage]

    return {
      strings: messages.value[currentLanguage].uppy_uploader as Record<
        string,
        string | Record<number, string>
      >,
      pluralize:
        metadata?.pluralize || ((count: number) => (count === 1 ? 0 : 1)),
    }
  })

  const addPlugin = <T extends typeof BasePlugin<any, TMeta, TBody>>(
    name: string,
    plugin: T,
    ...pluginOptions: OmitFirstArg<ConstructorParameters<T>>
  ) => {
    const existingPlugin = uppy.getPlugin(name)

    if (existingPlugin) {
      uppy.getPlugin(name)?.setOptions({ id: name, locale: uppyLocale.value, ...pluginOptions })

      return
    }

    uppy.use(plugin, ...pluginOptions)
    uppy
      .getPlugin(name)
      ?.setOptions({ id: name, locale: uppyLocale.value, ...pluginOptions })
  }

  const removePlugin = (name: string) => {
    const plugin = uppy.getPlugin(name)

    if (plugin) {
      uppy.removePlugin(plugin)
    }
  }

  const addFileRestriction = (restriction: string) => {
    const updatedFileTypes = new Set(options.restrictions?.allowedFileTypes)
    updatedFileTypes.add(restriction)
    options.restrictions = {
      ...options.restrictions,
      allowedFileTypes: Array.from(updatedFileTypes),
    }
  }

  const removeFileRestriction = (restriction: string) => {
    const updatedFileTypes = new Set(options.restrictions?.allowedFileTypes)
    updatedFileTypes.delete(restriction)
    options.restrictions = {
      ...options.restrictions,
      allowedFileTypes: Array.from(updatedFileTypes),
    }
  }

  watchEffect(() => {
    uppy.setOptions({ locale: uppyLocale.value, ...options })
  })

  return {
    uppy,
    addPlugin,
    removePlugin,
    addFileRestriction,
    removeFileRestriction,
    uploadError,
  }
}
